import {
  LuCalendarDays,
  LuScanLine,
  LuLeaf,
  LuCombine,
  LuFlower2,
  LuGraduationCap,
  LuShoppingBag,
  LuTrees,
  LuChevronRight,
  LuLayoutGrid,
} from 'react-icons/lu'
import { Link } from 'react-router'
import { ROUTES } from '@/constants/routes'
import {
  kandakeHerbalist,
  kandakeOutdoors,
  herbalWalkOne,
  herbalWalkPortrait,
  courseThree,
  courseWorkshop,
  productOne,
  productTwo,
} from '@/assets/siteMedia'

const featured = [
  {
    n: '01',
    title: '3 Month Integrated Health Programme',
    desc: 'A personalised 12-week plan with ongoing consultations, nutrition and lifestyle guidance, and regular progress reviews.',
    path: ROUTES.WORK_WITH_ME,
    tag: 'Key Programme',
    tagStyle: 'bg-green-50 text-green-700 border-green-200',
  },
  {
    n: '02',
    title: 'Bio-Scan Health Assessment',
    desc: 'A non-invasive assessment with a detailed report, personal feedback and a health summary with practical first steps.',
    path: ROUTES.PRODUCTS,
    tag: 'Now Enquiring',
    tagStyle: 'bg-gold-50 text-base-700 border-gold-200',
  },
  {
    n: '03',
    title: 'Herbal Consultations',
    desc: 'Personalised one-to-one consultations exploring root causes through herbal medicine, nutrition, lifestyle guidance, and holistic wellbeing.',
    path: ROUTES.SERVICES_CONSULTATIONS,
    tag: 'Now Enquiring',
    tagStyle: 'bg-green-50 text-green-700 border-green-200',
  },
]

const rest = [
  {
    n: '04',
    title: 'Integrated Health Consultation + Bio-Scan',
    desc: 'A two-hour consultation and Bio-Scan with integrated reporting and a follow-up Zoom session.',
    path: ROUTES.SERVICES_INTEGRATED_HEALTH,
  },
  {
    n: '05',
    title: "Women's Health",
    desc: 'Specialist support across menstrual health, fibroids, hormonal balance, fertility, menopause, and reproductive wellbeing.',
    path: ROUTES.WOMENS_HEALTH,
  },
  {
    n: '06',
    title: 'Courses & Workshops',
    desc: 'Transformational programmes in herbal medicine, holistic health, nutrition, and traditional healing systems.',
    path: ROUTES.SERVICES_COURSES,
  },
  {
    n: '07',
    title: 'Herbal Products',
    desc: 'Handcrafted remedies rooted in 20+ years of clinical experience and African & Caribbean healing traditions.',
    path: ROUTES.PRODUCTS,
  },
  {
    n: '08',
    title: 'Herbal Walks & Nature Education',
    desc: 'Immersive plant education reconnecting you with the healing intelligence of local plants and ecosystems.',
    path: ROUTES.TRAINING,
  },
]

const images = [
  kandakeOutdoors,
  productTwo,
  kandakeHerbalist,
  courseThree,
  herbalWalkPortrait,
  courseWorkshop,
  productOne,
  herbalWalkOne,
]
const serviceIcons = [
  LuCalendarDays,
  LuScanLine,
  LuLeaf,
  LuCombine,
  LuFlower2,
  LuGraduationCap,
  LuShoppingBag,
  LuTrees,
]

const services = [...featured, ...rest]

export const ServicesSection = () => (
  <section
    aria-labelledby="services-heading"
    className="border-t border-base-200 bg-base-50 py-16 sm:py-20 lg:py-24"
  >
    <div className="mx-auto max-w-6xl px-6">
      <div className="mb-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2
            id="services-heading"
            className="font-display text-3xl leading-tight tracking-tight text-base-900 sm:text-4xl lg:text-5xl"
          >
            Find your starting point.
          </h2>
        </div>
        <p className="max-w-sm text-base leading-7 text-base-600">
          Personal support, deeper learning, and a closer connection to your wellbeing.
        </p>
      </div>
      <div className="grid gap-5 lg:grid-cols-2 lg:gap-6">
        {services.map((service, i) => {
          const ServiceIcon = serviceIcons[i]
          return (
            <Link
              key={service.path}
              to={service.path}
              className="group grid min-w-0 overflow-hidden rounded-3xl border border-base-200 bg-white transition-colors hover:border-green-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-4 sm:grid-cols-[0.7fr_1fr] lg:grid-cols-[0.65fr_1fr]"
            >
              <div className="relative isolate overflow-hidden bg-base-100">
                <img
                  src={images[i]}
                  alt=""
                  loading="lazy"
                  className="aspect-[16/10] h-full w-full object-cover object-center motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-105 sm:absolute sm:inset-0 sm:aspect-auto"
                />
                {i === 0 && (
                  <span className="absolute left-4 top-4 rounded-full bg-green-950 px-3 py-1.5 text-xs font-medium text-base-50">
                    Key Programme
                  </span>
                )}
              </div>
              <div className="flex min-w-0 flex-col p-6 sm:min-h-72 lg:p-7">
                <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-green-50 text-green-700">
                  <ServiceIcon aria-hidden="true" className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl leading-tight tracking-tight text-base-900 transition-colors group-hover:text-green-800">
                  {service.title}
                </h3>
                <p className="mt-4 flex-1 text-sm leading-6 text-base-600">{service.desc}</p>
                <span className="mt-6 flex items-center justify-between gap-3 text-sm font-medium text-green-700">
                  Explore this service
                  <span
                    aria-hidden="true"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-green-200 bg-green-50 text-lg transition-colors group-hover:bg-green-700 group-hover:text-white"
                  >
                    <LuChevronRight aria-hidden="true" className="h-5 w-5" />
                  </span>
                </span>
              </div>
            </Link>
          )
        })}
      </div>
      <div className="mt-10 flex flex-col items-center justify-between gap-5 border-t border-base-200 pt-7 sm:flex-row">
        <p className="text-center text-sm leading-6 text-base-600 sm:text-left">
          Explore all eight services at your own pace.
        </p>
        <Link
          to={ROUTES.SERVICES}
          className="inline-flex min-h-12 shrink-0 items-center justify-center gap-3 rounded-full bg-green-700 px-7 text-sm font-medium text-white transition-colors hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-4"
        >
          View all services <LuLayoutGrid aria-hidden="true" className="h-4 w-4 shrink-0" />
        </Link>
      </div>
    </div>
  </section>
)
